import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();
    
    const observer = new MutationObserver((mutations, obs) => {
      const currencyWrapper = document.querySelector('.buckscc-currency-wrapper');
      if (currencyWrapper) {
        this.#assignRange();
        obs.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
    
    this.details.addEventListener('toggle', this.#handlePriceFilterInputWhenCurrencyChange, { signal });

    const filterWrapper = document.querySelector('.facets-toggle__wrapper');
    if (filterWrapper) filterWrapper.addEventListener('click', this.#handlePriceFilterInputWhenCurrencyChange, { signal });

    const minVisible = this.querySelector('.price-facet__input.min-input-visible');
    const maxVisible = this.querySelector('.price-facet__input.max-input-visible');
    const facetsClearAll = document.querySelectorAll('.facets__clear-all');
    const facetsClear = document.querySelector('.price-facet .facets__clear');
    const facetsRemoves = document.querySelectorAll('facet-remove-component');

    const handleInputChangeAndReload = () => {
        clearTimeout(this._reloadTimeout); // clear previous timer if any
        this._reloadTimeout = setTimeout(() => {
          window.location.reload();
        }, 1000); // wait 1 second
      };
      
    if (minVisible) {
      minVisible.addEventListener('change', handleInputChangeAndReload, { signal });
    }

    if (maxVisible) {
      maxVisible.addEventListener('change', handleInputChangeAndReload, { signal });
    }

    if (facetsClearAll) {
      facetsClearAll.forEach(item => {
        item.addEventListener('click', handleInputChangeAndReload, { signal });
      })
    }

    if (facetsClear) {
      facetsClear.addEventListener('click', handleInputChangeAndReload, { signal });
    }

    if (facetsRemoves) {
      facetsRemoves.forEach(item => {
        item.addEventListener('click', handleInputChangeAndReload, { signal });
      })
    }
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  #assignRange = () => {
    const minMax = this.querySelector('.min-input-max-value');
    
    if (!minMax) return;
    const minCurrency = minMax?.querySelector('.money.currency')?.getAttribute('bucks-currency') || 'USD';
    const isVND = minCurrency === 'VND';

    const priceRange = this.querySelector('.hide-when-empty');
    // const priceRangeMobile = document.querySelector('.price-range');
    
    const minVisible = this.querySelector('.price-facet__input.min-input-visible');
    const maxVisible = this.querySelector('.price-facet__input.max-input-visible');

    const params = new URLSearchParams(window.location.search);

    const gte = parseFloat(params.get('filter.v.price.gte'));
    const lte = parseFloat(params.get('filter.v.price.lte'));
    

    const exchangeRate = isVND ? 1 / 25000 : 1; // USD → VND
    const maxMax = this.querySelector('.max-input-max-value');

    const rawMin = !isNaN(gte) ? Math.round(gte / exchangeRate) : 0;
    const rawMax = !isNaN(lte) ? Math.round(lte / exchangeRate) : Math.round(maxMax.textContent.replace(/[₫₭€$¥£₹₽.]/g, '').trim());

    const formatVND = val => `${isVND ? '₫' : '$'}${Number(val).toLocaleString('vi-VN')}${isVND ? '' : '.00'}`;
    const formatVndWithoutCurrency = val => `${Number(val).toLocaleString('vi-VN')}${isVND ? '' : '.00'}`;

    if (priceRange && window.innerWidth >= 750) {
      if (gte || lte) {
        priceRange.innerHTML = `<span class="money">${formatVND(rawMin)}</span> – <span class="money">${formatVND(rawMax)}</span>`;
      }
    }
    
    // if (priceRangeMobile && window.innerWidth < 750) {
    //   priceRangeMobile.innerHTML = `<span class="money">${formatVND(rawMin)}</span> – <span class="money">${formatVND(rawMax)}</span>`;
    // }

    if (minVisible) {
      minVisible.value = !!gte ? formatVndWithoutCurrency(rawMin) : "";
    }

    if (maxVisible) {
      maxVisible.value = !!lte ? formatVndWithoutCurrency(rawMax) : "";
    }
  }

  #handlePriceFilterInputWhenCurrencyChange = () => {
    const minInput = this.querySelector('.price-facet__input.min-input'); // hidden USD input
    const maxInput = this.querySelector('.price-facet__input.max-input');
    const minVisible = this.querySelector('.price-facet__input.min-input-visible'); // new VND input
    const maxVisible = this.querySelector('.price-facet__input.max-input-visible');

    const minMax = this.querySelector('.min-input-max-value');
    const minCurrency = minMax?.querySelector('.money.currency')?.getAttribute('bucks-currency') || 'USD';
    const exchangeRate = minCurrency === 'VND' ? 25000 : 1;
    const isVND = minCurrency === 'VND';

    const formatNumber = (value) => value.toString().replace(/[^\d.]/g, '');
    const toUSD = (value) => (parseFloat(value) / exchangeRate).toFixed(2);

    if (!minInput || !maxInput || !minVisible || !maxVisible) return;

    minVisible.addEventListener('input', () => {
      const raw = formatNumber(minVisible.value);
      if (!isNaN(raw)) minInput.value = toUSD(raw);
    });

    maxVisible.addEventListener('input', () => {
      const raw = formatNumber(maxVisible.value);
      if (!isNaN(raw)) maxInput.value = toUSD(raw);
    });

    const labels = document.querySelectorAll('.price-facet__label');
    labels.forEach(label => {
      label.textContent = isVND ? '₫' : '$';
    });

    const maxMax = this.querySelector('.max-input-max-value');
    if (isVND) {
      maxVisible.placeholder = maxMax?.textContent.replace(/[₫₭€$¥£₹₽]/g, '').trim();
    } else {
      maxVisible.placeholder = toUSD(maxMax?.textContent);
    }

    this.#assignRange();
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
